import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import {
  AnalyticsService,
  LearnerPerformanceByGradeVM,
  LearnerPerformanceRequest
} from '../../services/api';

type GroupedBar = { name: string; series: { name: string; value: number }[] };

@Component({
  selector: 'app-learner-performance',
  templateUrl: './learner-performance.component.html',
  styleUrls: ['./learner-performance.component.scss']
})
export class LearnerPerformanceComponent implements OnInit, OnChanges {
  @Input() viewsize: [number, number] = [800, 500];
  @Input() disableResize: boolean = false;
  @Input() startDate?: Date | null;
  @Input() endDate?: Date | null;
  showXAxis = true;
  showYAxis = true;
  showLegend = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  xAxisLabel = 'Result';
  yAxisLabel = 'Number of learners';

  chartData: GroupedBar[] = [];
  loading = false;
  errorMsg = '';

  constructor(private analyticsService: AnalyticsService) {}

  ngOnInit(): void {
    this.fetchAllQuizzes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate'] || changes['endDate']) {
      this.fetchAllQuizzes();
    }
  }

  private fetchAllQuizzes(): void {
    this.loading = true;
    this.errorMsg = '';

    const body: LearnerPerformanceRequest = {
      resultField: 'gradeLetter',
      bucketSize: 10,
      startDateUtc: this.startDate ? this.startDate.toISOString() : undefined,
      endDateUtc: this.endDate ? this.endDate.toISOString() : undefined
      // omit quizId/actualQuizId/grade filters => ALL quizzes within range
    } as LearnerPerformanceRequest;

    // If your generated name differs, pick it from AnalyticsService and swap below.
    this.analyticsService.analyticsGetLearnerPerformanceByGradePost(body).subscribe({
      next: (rows: LearnerPerformanceByGradeVM[]) => {
        this.chartData = this.pivotToGroupedBar(rows || []);
        console.log('Learner performance data:', this.chartData);
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.errorMsg = 'Failed to load data.';
        this.loading = false;
      }
    });
  }

/**
 * Always produce 4 result buckets in this order:
 * B.E, A.E, M.E, E.E — even if some are missing in the backend.
 */
private pivotToGroupedBar(rows: LearnerPerformanceByGradeVM[]): GroupedBar[] {
  // Fixed results order
  const RESULTS_ORDER: string[] = ['B.E', 'A.E', 'M.E', 'E.E'];

  // Collect distinct grades (from whatever the API returns)
  const gradeSet = new Set<string>();
  for (let i = 0; i < rows.length; i++) {
    const g = rows[i] && (rows[i] as any).grade;
    if (typeof g === 'string' && g.length > 0) gradeSet.add(g);
  }
  const grades = Array.from(gradeSet).sort((a, b) => a.localeCompare(b));

  // Build chart data in the fixed result order
  const data: GroupedBar[] = [];
  for (let i = 0; i < RESULTS_ORDER.length; i++) {
    const result = RESULTS_ORDER[i];
    const series: { name: string; value: number }[] = [];

    for (let g = 0; g < grades.length; g++) {
      const grade = grades[g];

      // find row for this grade
      let row: any = null;
      for (let k = 0; k < rows.length; k++) {
        if ((rows[k] as any).grade === grade) { row = rows[k]; break; }
      }

      // default to 0 if this result is missing for the grade
      let count = 0;
      if (row && row.bars) {
        for (let b = 0; b < row.bars.length; b++) {
          const bar = (row.bars as any[])[b];
          if (bar && bar.result === result) { count = Number(bar.count) || 0; break; }
        }
      }

      series.push({ name: grade, value: count });
    }

    data.push({ name: result, series });
  }

  return data;
}

  onResize(event: any) {
  if (this.disableResize) return;
    const w = event && event.target && event.target.innerWidth ? event.target.innerWidth : 1200;
    this.viewsize = [Math.max(600, Math.floor(w * 0.75)), 500];
  }
}
